import ContentIllustration from '../../../components/ui/ContentIllustration';
import { useCallback, useEffect, useState } from 'react';
import { Check, Clock, AlertCircle, Gift, PenLine } from 'lucide-react';
import api from '../../../services/api';
import QrcPackCheckoutModal from '../../../components/stripe/QrcPackCheckoutModal';

// Onglet Achats du compte : historique des abonnements + codes cadeaux achetés
// + corrections rédigées (solde, packs, achat).
// Charge ses données au montage (l'onglet n'est rendu que lorsqu'il est actif).
export default function CompteAchats() {
    const [purchases, setPurchases] = useState([]);
    const [giftCodes, setGiftCodes] = useState([]);
    const [qrc, setQrc] = useState(null); // { balance, packs, offers } | null si non installé
    const [loading, setLoading] = useState(true);
    const [buyOpen, setBuyOpen] = useState(false);
    const justBought = new URLSearchParams(window.location.search).get('qrc_pack') === 'success';

    const load = useCallback(() => {
        return api.get('/user/purchases.php')
            .then(({ data }) => {
                setPurchases(data.data?.purchases || []);
                setGiftCodes(data.data?.gift_codes || []);
                setQrc(data.data?.qrc_credits || null);
            })
            .catch(e => console.warn("Erreur:", e));
    }, []);

    useEffect(() => { load().finally(() => setLoading(false)); }, [load]);

    const hasActiveSub = purchases.some(p => p.statut === 'actif');
    const qrcPacks = qrc?.packs || [];
    const qrcOffers = qrc?.offers || [];
    // La section n'apparaît que si l'utilisateur a déjà un solde ou des packs, ou si une thématique
    // est en mode crédits (sinon l'offre d'achat n'aurait aucun usage : corrections incluses partout)
    const showQrc = qrc && (qrcPacks.length > 0 || (qrc.balance || 0) > 0 || (qrcOffers.length > 0 && qrc.credits_active));
    const kindLabels = { abonnement: 'Inclus', achat: 'Acheté', offert: 'Offert' };

    const renderQrc = () => showQrc && (
        <>
            <h3 className="compte-section-label" style={{ marginTop: (purchases.length > 0 || giftCodes.length > 0) ? 32 : 0 }}>
                <PenLine size={18} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                Corrections rédigées
            </h3>
            {justBought && <p className="compte-purchase-dates" style={{ color: 'var(--success, #10b981)', marginBottom: 8 }}>Paiement reçu : tes corrections sont ajoutées dès la confirmation de Stripe, généralement en quelques secondes.</p>}
            <div className="compte-qrc-balance">
                <strong>{qrc.balance}</strong> correction{qrc.balance > 1 ? 's' : ''} disponible{qrc.balance > 1 ? 's' : ''}
                {qrc.balance > 0 && !hasActiveSub && <span className="compte-purchase-dates"> · en réserve : réabonne-toi à une thématique pour les utiliser</span>}
                {qrcOffers.length > 0 && (
                    <button type="button" className="btn btn-sm btn-primary" style={{ marginLeft: 12 }} onClick={() => setBuyOpen(true)}>
                        Acheter des corrections
                    </button>
                )}
            </div>
            {qrcPacks.length > 0 && (
                <div className="compte-purchase-list">
                    {qrcPacks.map(p => (
                        <div key={`qrc-${p.id}`} className={`compte-purchase-card ${p.refunded ? 'compte-purchase-annule' : ''}`}>
                            <div className="compte-purchase-icon"><PenLine size={16} /></div>
                            <div className="compte-purchase-info">
                                <strong>{p.label}</strong>
                                <span className="compte-purchase-duree">{p.credits} correction{p.credits > 1 ? 's' : ''} · reste {p.remaining}{p.reserved > 0 ? ` (${p.reserved} en cours)` : ''}</span>
                                <span className="compte-purchase-dates">Le {new Date(String(p.purchased_at).replace(' ', 'T')).toLocaleDateString('fr-FR')}</span>
                            </div>
                            <div className="compte-purchase-right">
                                <span className="compte-purchase-prix">{p.price_eur > 0 ? `${p.price_eur.toFixed(2).replace('.', ',')}€` : 'Offert'}</span>
                                <span className="compte-purchase-badge">{p.refunded ? 'Remboursé' : (kindLabels[p.kind] || p.kind)}</span>
                            </div>
                        </div>
                    ))}
                </div>
            )}
            {buyOpen && <QrcPackCheckoutModal onClose={() => setBuyOpen(false)} onSuccess={() => load()} />}
        </>
    );

    if (loading) return <div className="loading">Chargement...</div>;
    if (purchases.length === 0 && giftCodes.length === 0 && showQrc) {
        return <div className="compte-purchases">{renderQrc()}</div>;
    }
    if (purchases.length === 0 && giftCodes.length === 0) {
        return (
            <div className="compte-empty">
                <ContentIllustration name="gift" size={64} />
                <p>Aucun achat pour le moment.</p>
                <p className="compte-empty-sub">Abonnez-vous à une thématique pour débloquer tout le contenu.</p>
            </div>
        );
    }

    const giftStatusLabels = { AVAILABLE: 'En attente', REDEEMED: 'Utilisé', EXPIRED: 'Expiré', REVOKED: 'Annulé' };
    const giftStatusColors = { AVAILABLE: 'var(--primary)', REDEEMED: 'var(--success, #10b981)', EXPIRED: 'var(--text-muted)', REVOKED: 'var(--danger, #ef4444)' };

    return (
        <div className="compte-purchases">
            {purchases.length > 0 && <>
                <h3 className="compte-section-label">Historique des achats</h3>
                <div className="compte-purchase-list">
                    {purchases.map((p) => (
                        <div key={p.id} className={`compte-purchase-card compte-purchase-${p.statut}`}>
                            <div className="compte-purchase-icon">
                                {p.statut === 'actif' ? <Check size={16} strokeWidth={2.5} />
                                    : p.statut === 'expire' ? <Clock size={16} strokeWidth={2} />
                                    : <AlertCircle size={16} strokeWidth={2} />}
                            </div>
                            <div className="compte-purchase-info">
                                <strong>{p.scope_label}</strong>
                                <span className="compte-purchase-duree">{p.duree_label}</span>
                                <span className="compte-purchase-dates">
                                    Du {new Date(p.debut).toLocaleDateString('fr-FR')} au {new Date(p.fin).toLocaleDateString('fr-FR')}
                                </span>
                                {p.gift_buyer_name && (
                                    <span className="compte-purchase-dates" style={{ color: 'var(--primary, #8B5CF6)' }}>
                                        <Gift size={11} style={{ verticalAlign: 'middle', marginRight: 3 }} />
                                        Offert par {p.gift_buyer_name}
                                        {p.gift_redeemed_at && <> · activé le {new Date(p.gift_redeemed_at).toLocaleDateString('fr-FR')}</>}
                                    </span>
                                )}
                            </div>
                            <div className="compte-purchase-right">
                                <span className="compte-purchase-prix">
                                    {parseInt(p.is_gift) ? 'Offert' : `${p.prix}€`}
                                </span>
                                <span className={`compte-purchase-badge compte-purchase-badge-${p.statut}`}>
                                    {p.statut === 'actif' ? 'Actif' : p.statut === 'expire' ? 'Expiré' : 'Annulé'}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            </>}

            {giftCodes.length > 0 && <>
                <h3 className="compte-section-label" style={{ marginTop: purchases.length > 0 ? 32 : 0 }}>
                    <Gift size={18} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                    Codes cadeaux achetés
                </h3>
                <div className="compte-purchase-list">
                    {giftCodes.map((g) => (
                        <div key={`gift-${g.id}`} className="compte-purchase-card" style={{ borderLeft: `3px solid ${giftStatusColors[g.status] || 'var(--border)'}` }}>
                            <div className="compte-purchase-icon" style={{ color: giftStatusColors[g.status] }}>
                                <Gift size={16} />
                            </div>
                            <div className="compte-purchase-info">
                                <strong>{g.scope_label}</strong>
                                <span className="compte-purchase-duree">{g.duree_label}</span>
                                <span style={{ fontFamily: 'monospace', fontSize: '0.85em', letterSpacing: 1, color: 'var(--primary)' }}>
                                    {g.code}
                                </span>
                                {g.recipient_email && (
                                    <span className="compte-purchase-dates">Envoyé à {g.recipient_email}</span>
                                )}
                                {g.status === 'REDEEMED' && g.redeemed_by_name && (
                                    <span className="compte-purchase-dates">Utilisé par {g.redeemed_by_name}</span>
                                )}
                            </div>
                            <div className="compte-purchase-right">
                                <span className="compte-purchase-prix">{parseFloat(g.price_paid) > 0 ? `${g.price_paid}€` : '—'}</span>
                                <span className="compte-purchase-badge" style={{ color: giftStatusColors[g.status] }}>
                                    {giftStatusLabels[g.status] || g.status}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            </>}

            {renderQrc()}
        </div>
    );
}
